<script setup lang="ts">
import { Text } from 'vue'
import { resolveIcon } from '~/utils/vuetify-icons'
import { categoryIconSvg } from '~/composables/useCategoryIcons'

const props = withDefaults(defineProps<{
  icon?: string
  size?: 'x-small' | 'small' | 'default' | 'large' | 'x-large' | number
  color?: string
  tag?: string
}>(), {
  size: 'default',
  tag: 'span',
})

const slots = useSlots()

// Effektiver Icon-Name: prop ODER slot-text
const effectiveIcon = computed(() => {
  if (props.icon) return props.icon
  // Slot-Content als Icon-Name (Vuetify-Kompatibilität: <MIcon>name</MIcon>)
  const slot = slots.default?.()
  const firstNode = slot?.[0]
  if (slot?.length === 1 && firstNode?.type === Text) {
    const text = (firstNode.children as string)?.trim()
    if (text && !text.includes('<')) return text
  }
  return null
})

const sizeMap: Record<string, string> = {
  'x-small': '16px',
  'small': '20px',
  'default': '24px',
  'large': '32px',
  'x-large': '40px',
}

const computedSize = computed(() => {
  if (typeof props.size === 'number') return `${props.size}px`
  if (typeof props.size === 'string' && /^\d+$/.test(props.size)) return `${props.size}px`
  return sizeMap[props.size] || sizeMap.default
})

// 1. Solar SVG Body (komplexes SVG mit <g>, <path> etc.)
const solarBody = computed(() => {
  if (!effectiveIcon.value) return null
  return categoryIconSvg(effectiveIcon.value)
})

// 2. MDI SVG Path (einzelner d-String)
const svgPath = computed(() => {
  if (!effectiveIcon.value || solarBody.value) return null
  return resolveIcon(effectiveIcon.value)
})

const colorStyle = computed(() => {
  if (!props.color) return undefined
  const varMap: Record<string, string> = {
    primary: 'var(--m-primary)',
    secondary: 'var(--m-secondary)',
    accent: 'var(--m-accent)',
    error: 'var(--m-error)',
    warning: 'var(--m-warning)',
    info: 'var(--m-info)',
    success: 'var(--m-success)',
    white: '#ffffff',
    grey: '#9e9e9e',
    'grey-lighten-1': '#bdbdbd',
    'grey-lighten-2': '#e0e0e0',
    'grey-darken-1': '#757575',
    'grey-darken-2': '#616161',
    'surface-variant': 'var(--m-on-surface-variant)',
  }
  return varMap[props.color] || props.color
})
</script>

<template>
  <component
    :is="tag"
    class="m-icon"
    :style="{
      width: computedSize,
      height: computedSize,
      color: colorStyle,
    }"
    aria-hidden="true"
  >
    <!-- Solar Icons (SVG body mit <g>, <path>, stroke-basiert) -->
    <svg
      v-if="solarBody"
      viewBox="0 0 24 24"
      :width="computedSize"
      :height="computedSize"
      fill="none"
      v-html="solarBody"
    />
    <!-- MDI Icons (einzelner Path d-String) -->
    <svg
      v-else-if="svgPath"
      viewBox="0 0 24 24"
      :width="computedSize"
      :height="computedSize"
      fill="currentColor"
    >
      <path :d="svgPath" />
    </svg>
    <slot v-else-if="!effectiveIcon" />
  </component>
</template>

<style scoped>
.m-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 1;
  vertical-align: middle;
}
.m-icon svg {
  fill: currentColor;
}
</style>
